import React, { useEffect, useState } from 'react';
import { getCauzioniSubProductDocuments } from '../../services/axios-client/axeCauzioni';
import { BACKEND_API_URL } from '../../config';

function DocumentList({ title, documents }) {
  if (!documents.length) return null;

  return (
    <div className="cauzioni-doc-group">
      <h3>{title}</h3>
      <ul>
        {documents.map((doc) => (
          <li key={doc.document_type_id}>{doc.name}</li>
        ))}
      </ul>
    </div>
  );
}

function DownloadableDocsList({ documents, baseUrl }) {
  if (!documents || !documents.length) return null;

  const badgeClass = { questionario: 'cauzioni-download-badge-questionario', set_informativo: 'cauzioni-download-badge-set-informativo', pog: 'cauzioni-download-badge-pog' };
  const badgeLabel = { questionario: 'QUESTIONARIO', set_informativo: 'SET INFORMATIVO', pog: 'POG' };
  const apiUrl = baseUrl || BACKEND_API_URL;

  return (
    <div className="cauzioni-downloadable-docs">
      <h3>Documenti da scaricare</h3>
      {documents.map((doc) => (
        <div className="cauzioni-download-item" key={doc.filename}>
          <span className={`cauzioni-download-badge ${badgeClass[doc.type] || ''}`}>
            {badgeLabel[doc.type] || doc.type}
          </span>
          <span className="cauzioni-download-name">{doc.name}</span>
          <a
            className="cauzioni-download-btn"
            href={`${apiUrl}/surety-bonds/downloadable-document/${encodeURIComponent(doc.filename)}`}
          >
            Scarica
          </a>
        </div>
      ))}
    </div>
  );
}

function RiskDocuments({ risk }) {
  const hasNone = !risk.required.length && !risk.on_request.length;
  const hasDownloads = risk.downloadable_documents && risk.downloadable_documents.length > 0;

  if (hasNone && !hasDownloads) {
    return <p className="cauzioni-empty">Nessun documento richiesto per questo rischio.</p>;
  }

  return (
    <>
      {risk.description && (
        <div className="cauzioni-modal-copy">
          {risk.subtitle && <p className="cauzioni-modal-reference">{risk.subtitle}</p>}
          {risk.description.split('\n\n').map((para) => (
            <p key={para.slice(0, 60)}>{para}</p>
          ))}
        </div>
      )}
      <DownloadableDocsList documents={risk.downloadable_documents} />
      <DocumentList title="Documenti obbligatori" documents={risk.required} />
      <DocumentList title="Documenti su richiesta dell'assuntore" documents={risk.on_request} />
    </>
  );
}

function CauzioniInfoModal({ subProduct, onClose, riskId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    getCauzioniSubProductDocuments(subProduct.id)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [subProduct]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* Four of the six multi-rischio sub-products carry an identical document set on every
     rischio (Doganali's 20 all match). Showing it once beats repeating it twenty times. */
  /* With a riskId the modal describes that one rischio, which is what the Info button on the
     rischio screen asks for; without it, it summarises the whole garanzia. */
  const risks = riskId && data
    ? data.risks.filter((r) => r.id === riskId)
    : data?.risks ?? [];
  const collapse = riskId ? true : data?.identical_across_risks;

  return (
    <div
      className="cauzioni-modal-backdrop"
      data-testid="cauzioni-info-modal"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="cauzioni-modal"
        role="dialog"
        aria-modal="true"
        aria-label={subProduct.name}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cauzioni-modal-head">
          {/* Opened from a rischio card, the modal is about that rischio — showing the
              garanzia's name instead made it look like the wrong thing had opened. */}
          <h2>{riskId && risks[0] ? risks[0].name : subProduct.name}</h2>
          <button type="button" className="btn-close" onClick={onClose} aria-label="Chiudi" />
        </div>

        <div className="cauzioni-modal-body">
          {/* The garanzia's own copy comes first: it explains what the polizza is for, which is
              the question a broker is asking when they open Info. The document list answers the
              follow-up. Both are about the same garanzia even when a rischio titles the modal. */}
          {(subProduct.description || subProduct.description_long) && (
            <div className="cauzioni-modal-copy" data-testid="cauzioni-info-copy">
              {subProduct.description && (
                <p className="cauzioni-modal-reference">{subProduct.description}</p>
              )}
              {subProduct.description_long?.split('\n\n').map((para) => (
                <p key={para.slice(0, 60)}>{para}</p>
              ))}
            </div>
          )}

          {loading && <p className="cauzioni-empty">Caricamento documenti...</p>}

          {!loading && error && (
            <p className="cauzioni-error">
              Impossibile caricare i documenti. Riprova più tardi.
            </p>
          )}

          {!loading && !error && data && collapse && (
            <>
              {!riskId && data.risks.length > 1 && (
                <p className="cauzioni-empty mb-3">
                  {`Documenti validi per tutti i ${data.risks.length} rischi di questa garanzia.`}
                </p>
              )}
              <RiskDocuments risk={risks[0]} />
            </>
          )}

          {!loading && !error && data && !collapse && risks.map((risk) => (
            <div key={risk.id} className="cauzioni-risk-block">
              <p className="cauzioni-risk-name">{risk.name}</p>
              <RiskDocuments risk={risk} />
            </div>
          ))}
        </div>

        <div className="cauzioni-modal-foot">
          <button type="button" className="btn btn-success" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

export default CauzioniInfoModal;
